import React, { ReactNode, useEffect, useState, useRef } from "react";
import {
  animationRegistry,
  AnimationController,
} from "../lib/utils/animationRegistry";
import {
  fadeInWorkflow,
  fadeOutWorkflow,
  verticalFlipWorkflow,
  shrinkWorkflow,
  shrinkBounceWorkflow,
  cardMoveWorkflow,
} from "../lib/utils/animation/animationSteps";
import {
  cardFlipWorkflow,
  flipAtCenterWorkflow,
  shrinkFlipWorkflow,
  centerToPositionWorkflow,
} from "../lib/utils/animation/animationWorkflows";
import {
  AnimationWorkflow,
  useAnimationWorkflow,
  AnimationStep as WorkflowAnimationStep,
} from "@/lib/hooks/useAnimationWorkflow";
import { turnOffAnimations } from "@/lib/debug/debugFlags";

// Debug variable to speed up all animations

type SimpleAnimationType =
  | "fade-in"
  | "fade-out"
  | "vertical-flip"
  | "shrink"
  | "shrink-bounce"
  | "card-move"
  | "card-flip"
  | "flip-at-center"
  | "shrink-flip"
  | "center-to-position";

type AnimationStepConfig = {
  type?: SimpleAnimationType;
  duration?: number;
  [key: string]: any;
};

type TriggerStepConfig = {
  trigger: string;
};

interface AnimationProps {
  children: ReactNode;
  simple?: SimpleAnimationType | WorkflowAnimationStep | AnimationWorkflow;
  steps?: (
    | SimpleAnimationType
    | WorkflowAnimationStep
    | AnimationStepConfig
    | TriggerStepConfig
  )[];
  trigger?: "mount" | "manual" | "click";
  animationId?: string;
  onStart?: () => void;
  onComplete?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const getWorkflowForSimpleAnimation = (
  type: SimpleAnimationType,
  config?: Omit<AnimationStepConfig, "type"> & { onComplete?: () => void },
): AnimationWorkflow => {
  const duration = turnOffAnimations ? 50 : config?.duration;
  let workflow: AnimationWorkflow;

  switch (type) {
    case "fade-in":
      workflow = fadeInWorkflow(duration || 500);
      break;
    case "fade-out":
      workflow = fadeOutWorkflow(duration || 500);
      break;
    case "vertical-flip":
      workflow = verticalFlipWorkflow(duration || 2000);
      break;
    case "shrink":
      workflow = shrinkWorkflow(duration || 300);
      break;
    case "shrink-bounce":
      workflow = shrinkBounceWorkflow(duration || 200);
      break;
    case "card-move":
      workflow = cardMoveWorkflow(
        config?.targetX || "-40vw",
        config?.targetY || "-20vh",
        config?.targetScale || 0.4,
        config?.onMoveComplete,
      );
      break;
    case "card-flip":
      workflow = cardFlipWorkflow(config?.onFlipComplete);
      break;
    case "flip-at-center":
      workflow = flipAtCenterWorkflow(duration || 2000);
      break;
    case "shrink-flip":
      workflow = shrinkFlipWorkflow(
        config?.shrinkDuration || 200,
        config?.flipDuration || 2000,
      );
      break;
    case "center-to-position":
      workflow = centerToPositionWorkflow(duration || 1500);
      break;
    default:
      workflow = fadeInWorkflow(500);
  }

  if (config?.onComplete) {
    const originalComplete = workflow.onWorkflowComplete;
    workflow = {
      ...workflow,
      onWorkflowComplete: () => {
        if (originalComplete) originalComplete();
        config.onComplete?.();
      },
    };
  }

  return workflow;
};

const createWorkflowFromSimple = (
  simple: SimpleAnimationType | WorkflowAnimationStep | AnimationWorkflow,
  onComplete?: () => void,
): AnimationWorkflow => {
  // If it's already a complete workflow
  if (typeof simple === "object" && "steps" in simple) {
    const workflow = simple as AnimationWorkflow;

    // Apply turnOffAnimations override to all steps
    const modifiedSteps = turnOffAnimations
      ? workflow.steps.map((step) => ({ ...step, duration: 50 }))
      : workflow.steps;

    const modifiedWorkflow = { ...workflow, steps: modifiedSteps };

    if (onComplete) {
      const originalComplete = modifiedWorkflow.onWorkflowComplete;
      return {
        ...modifiedWorkflow,
        onWorkflowComplete: () => {
          if (originalComplete) originalComplete();
          onComplete();
        },
      };
    }
    return modifiedWorkflow;
  }

  // If it's a single animation step
  if (typeof simple === "object" && "id" in simple) {
    const animationStep = simple as WorkflowAnimationStep;
    const step = turnOffAnimations
      ? { ...animationStep, duration: 50 }
      : animationStep;
    return {
      id: "custom-step",
      name: "Custom Animation Step",
      steps: [step],
      onWorkflowComplete: onComplete,
    };
  }

  // If it's a simple animation type string
  return getWorkflowForSimpleAnimation(simple as SimpleAnimationType, {
    onComplete,
  });
};

const createStepsWorkflow = (
  steps: (
    | SimpleAnimationType
    | WorkflowAnimationStep
    | AnimationStepConfig
    | TriggerStepConfig
  )[],
  onComplete?: () => void,
): AnimationWorkflow => {
  const workflowSteps = steps.flatMap((step) => {
    // Check if it's a trigger step
    if (typeof step === "object" && "trigger" in step) {
      // Create a zero-duration step that triggers another animation
      return [
        {
          id: "trigger-step",
          name: "Trigger Animation",
          duration: 0,
          onComplete: () => {
            animationRegistry.trigger(step.trigger);
          },
        },
      ];
    }

    // Check if it's already a complete AnimationStep
    if (
      typeof step === "object" &&
      "id" in step &&
      "name" in step &&
      "duration" in step
    ) {
      const animationStep = step as WorkflowAnimationStep;
      // Apply turnOffAnimations override
      if (turnOffAnimations) {
        return [{ ...animationStep, duration: 50 }];
      }
      return [animationStep];
    }

    // Regular animation step config
    const isConfig = typeof step === "object";
    const type = isConfig
      ? (step as AnimationStepConfig).type
      : (step as SimpleAnimationType);

    // If type is not provided in config, skip this step
    if (isConfig && !type) {
      return [];
    }

    const config = isConfig ? (step as AnimationStepConfig) : {};

    // Apply turnOffAnimations override
    if (turnOffAnimations && config.duration !== undefined) {
      config.duration = 50;
    }

    const workflow = getWorkflowForSimpleAnimation(
      type as SimpleAnimationType,
      config,
    );
    return workflow.steps;
  });

  return {
    id: "custom-steps",
    name: "Custom Steps Animation",
    steps: workflowSteps,
    onWorkflowComplete: onComplete,
  };
};

export const Animation: React.FC<AnimationProps> = ({
  children,
  simple,
  steps,
  trigger = "mount",
  animationId,
  onStart,
  onComplete,
  className = "",
  style = {},
}) => {
  // Extract initial opacity from style prop, default to 1
  const initialOpacity = typeof style.opacity === "number" ? style.opacity : 1;
  // Extract initial transform from style prop, or set to first step's transform if steps exist
  let initialTransform =
    typeof style.transform === "string" ? style.transform : "";

  // If we have steps and no explicit initial transform, use the first step's transform
  if (!initialTransform && steps && steps.length > 0) {
    const firstStep = steps[0];
    if (
      typeof firstStep === "object" &&
      "transform" in firstStep &&
      firstStep.transform
    ) {
      // Build the transform string for the first step
      const transform = firstStep.transform;
      const parts: string[] = [];
      if (transform.translateX !== undefined) {
        parts.push(`translateX(${transform.translateX})`);
      }
      if (transform.translateY !== undefined) {
        parts.push(`translateY(${transform.translateY})`);
      }
      if (transform.scale !== undefined) {
        parts.push(`scale(${transform.scale})`);
      }
      initialTransform = parts.join(" ");
    }
  }

  const { startWorkflow, animatedStyle, isRunning, resetWorkflow } =
    useAnimationWorkflow(initialOpacity, initialTransform);
  const [hasTriggered, setHasTriggered] = useState(false);
  const controllerRef = useRef<AnimationController | null>(null);
  const mountTriggeredRef = useRef(false);

  // Manual trigger method
  const triggerAnimation = () => {
    if (isRunning) return;

    // Reset to initial state first
    resetWorkflow();

    // Small delay to ensure reset is processed
    setTimeout(() => {
      let workflow: AnimationWorkflow;

      if (simple) {
        workflow = createWorkflowFromSimple(simple, onComplete);
      } else if (steps) {
        workflow = createStepsWorkflow(steps, onComplete);
      } else {
        return;
      }

      if (onStart) {
        onStart();
      }
      startWorkflow(workflow);
    }, 10);
  };

  // Reset method
  const reset = () => {
    resetWorkflow();
    setHasTriggered(false);
    mountTriggeredRef.current = false;
  };

  // Click handler
  const handleClick = () => {
    if (trigger === "click") {
      triggerAnimation();
    }
  };

  useEffect(() => {
    if (
      trigger === "mount" &&
      !hasTriggered &&
      !isRunning &&
      !mountTriggeredRef.current
    ) {
      mountTriggeredRef.current = true;

      let workflow: AnimationWorkflow;

      if (simple) {
        workflow = createWorkflowFromSimple(simple, onComplete);
      } else if (steps) {
        workflow = createStepsWorkflow(steps, onComplete);
      } else {
        return;
      }

      // Add start callback
      if (onStart) {
        const originalComplete = workflow.onWorkflowComplete;
        workflow = {
          ...workflow,
          onWorkflowComplete: () => {
            if (originalComplete) originalComplete();
            if (onComplete) onComplete();
            mountTriggeredRef.current = false; // Reset for potential future triggers
          },
        };
        onStart();
      } else {
        // If no onStart, still reset the ref when workflow completes
        const originalComplete = workflow.onWorkflowComplete;
        workflow = {
          ...workflow,
          onWorkflowComplete: () => {
            if (originalComplete) originalComplete();
            mountTriggeredRef.current = false; // Reset for potential future triggers
          },
        };
      }

      startWorkflow(workflow);
      setHasTriggered(true);
    }
  }, [simple, steps, trigger, hasTriggered, onStart, onComplete, isRunning]);

  // Register animation in registry if animationId is provided
  useEffect(() => {
    if (animationId) {
      const controller: AnimationController = {
        triggerAnimation,
        reset,
        isRunning,
      };

      controllerRef.current = controller;
      animationRegistry.register(animationId, controller);

      return () => {
        animationRegistry.unregister(animationId);
      };
    }
  }, [animationId, isRunning]);

  // Expose methods for manual control
  React.useImperativeHandle(React.createRef(), () => ({
    trigger: triggerAnimation,
    reset,
  }));

  // Separate opacity and transform from other styles since they're handled by the hook
  const { opacity: _, transform: __, ...otherStyles } = style;

  return (
    <div
      className={`animation-wrapper ${className}`}
      style={{
        ...otherStyles,
        ...animatedStyle,
        transformOrigin: "center center",
      }}
      onClick={handleClick}
    >
      {children}
    </div>
  );
};

export default Animation;
